import { useMemo } from "react";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";
import { getStartOfNextMonth } from "../../../../../helpers/date";

const resetDateFormat = Intl.DateTimeFormat(
  window.navigator.language || "en-US",
  {
    month: "long",
    day: "numeric",
  }
);

export function useQuotaViewsAlert() {
  const { currentOrganization } = useCurrentOrganizationStore();

  const organizationSettings = useMemo(
    () => currentOrganization.model?.organization_settings || {},
    [currentOrganization.model]
  );

  if (!currentOrganization.model) {
    return null;
  }

  const isBlocked = Boolean(Number(organizationSettings.quota_views_blocked));
  const isAbove100 = Boolean(
    Number(organizationSettings.quota_views_above_100)
  );
  const isAbove95 = Boolean(Number(organizationSettings.quota_views_above_95));
  const isAbove75 = Boolean(Number(organizationSettings.quota_views_above_75));

  if (isBlocked) {
    const blockedDate = Number.parseInt(
      organizationSettings.quota_views_blocked_at,
      10
    );
    const baseDate = Number.isNaN(blockedDate)
      ? new Date()
      : new Date(blockedDate * 1000);
    const resetDate = resetDateFormat.format(getStartOfNextMonth(baseDate));

    return {
      id: "quotaViewsBlocked",
      variant: "critical",
      icon: "AlertCircle",
      title: "Widgets paused",
      body: `Your quota resets ${resetDate}. Upgrade to restore them now.`,
      action: { label: "Restore now", to: "/billing" },
    };
  }

  if (isAbove100) {
    return {
      id: "quotaViewsAbove100",
      variant: "critical",
      icon: "AlertCircle",
      title: "View limit reached",
      body: "Widgets stop displaying in 5 days unless you upgrade.",
      action: { label: "Upgrade now", to: "/billing" },
    };
  }

  if (isAbove95) {
    return {
      id: "quotaViewsAbove95",
      variant: "warning",
      icon: "AlertTriangle",
      title: "Running low on views",
      body: "At current traffic you'll hit your limit soon.",
      action: { label: "Review your plan", to: "/billing" },
    };
  }

  if (isAbove75) {
    return {
      id: "quotaViewsAbove75",
      variant: "warning",
      icon: "AlertTriangle",
      title: "Approaching your view limit",
      body: "You've used 75% of your monthly views.",
      action: { label: "Upgrade for headroom", to: "/billing" },
    };
  }

  return null;
}

export default useQuotaViewsAlert;
